import React, { useMemo } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { ArrowUpRight, Stethoscope, FlaskConical, Leaf, Filter } from "lucide-react";
import SEO from "@/components/SEO";
import Breadcrumbs from "@/components/Breadcrumbs";
import { Reveal, Stagger, Item } from "@/components/Reveal";
import { useFetch } from "@/lib/useData";

const FILTERS = [
  { slug: "all", label: "All services", icon: Filter },
  { slug: "medical-center", label: "Medical Center", icon: Stethoscope },
  { slug: "medical-lab", label: "Medical Lab", icon: FlaskConical },
  { slug: "pharmacy-wellness", label: "Pharmacy & Wellness", icon: Leaf },
];

export default function Services() {
  const { data: services } = useFetch("/services");
  const [params, setParams] = useSearchParams();
  const filter = params.get("division") || "all";
  const setFilter = (slug) => {
    if (slug === "all") setParams({}, { replace: true });
    else setParams({ division: slug }, { replace: true });
  };

  const filtered = useMemo(() => {
    if (!services) return [];
    return filter === "all" ? services : services.filter((s) => s.division_slug === filter);
  }, [services, filter]);

  return (
    <>
      <SEO title="Services | Infinity Medical Group" description="Explore the full range of services offered across IMG's medical, diagnostic, and pharmacy & wellness divisions." />
      <Breadcrumbs />

      <section className="bg-white pt-6 pb-10 lg:pb-14" data-testid="services-hero">
        <div className="container">
          <span className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.22em] font-semibold text-teal">
            <span className="h-px w-6 bg-teal" /> Our services
          </span>
          <h1 className="mt-5 font-display font-bold text-4xl md:text-6xl text-navy-800 tracking-tight max-w-3xl">
            A comprehensive suite of healthcare services.
          </h1>
          <p className="mt-6 max-w-2xl text-lg text-navy-700/75">
            Choose a division to focus the list. Every service is delivered by board-certified clinicians and quality-controlled processes.
          </p>
        </div>
      </section>

      {/* FILTER CHIPS */}
      <section className="bg-soft-gray sticky top-[72px] z-10 border-y border-navy-50">
        <div className="container py-4 flex flex-wrap gap-2" data-testid="services-filter">
          {FILTERS.map((f) => {
            const Icon = f.icon;
            const active = filter === f.slug;
            return (
              <button
                key={f.slug}
                data-testid={`filter-${f.slug}`}
                onClick={() => setFilter(f.slug)}
                className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold transition-colors ${
                  active ? "bg-navy text-white" : "bg-white text-navy-700 hover:bg-navy-50"
                }`}
              >
                <Icon className="h-4 w-4" /> {f.label}
              </button>
            );
          })}
        </div>
      </section>

      <section className="bg-white py-16 lg:py-20">
        <div className="container">
          {filtered.length === 0 ? (
            <p className="text-navy-700/75 text-lg">No services in this division yet.</p>
          ) : (
            <Stagger className="grid gap-5 md:grid-cols-2 lg:grid-cols-3">
              {filtered.map((s) => (
                <Item key={s.slug}>
                  <Link
                    to={`/services/${s.slug}`}
                    data-testid={`service-card-${s.slug}`}
                    className="group block h-full p-7 rounded-2xl bg-soft-gray border border-navy-50 hover:bg-white hover:shadow-card transition-all"
                  >
                    <span className="text-[11px] uppercase tracking-[0.22em] font-semibold text-teal">
                      {FILTERS.find((f) => f.slug === s.division_slug)?.label || "Service"}
                    </span>
                    <h3 className="mt-3 font-display font-semibold text-xl text-navy-800 group-hover:text-teal transition-colors">
                      {s.title}
                    </h3>
                    <p className="mt-3 text-sm leading-relaxed text-navy-700/75 line-clamp-3">{s.summary}</p>
                    <span className="mt-5 inline-flex items-center gap-1.5 text-sm font-semibold text-navy group-hover:text-teal">
                      View details <ArrowUpRight className="h-4 w-4" />
                    </span>
                  </Link>
                </Item>
              ))}
            </Stagger>
          )}
        </div>
      </section>
    </>
  );
}
